/**
 * ImageViewer Component
 *
 * Displays image files with zoom, pan, and fit functionality.
 * Supports PNG, JPG, JPEG, GIF, WebP, SVG, ICO, and BMP formats.
 */

import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { downloadWorkspaceFile } from '@/lib/downloadFile';
import { api } from '@/services/api';
import {
  ZoomIn,
  ZoomOut,
  Maximize,
  RotateCcw,
  Move,
  Download,
  Loader2,
  AlertCircle,
  Image as ImageIcon,
} from 'lucide-react';

// ============================================
// Types
// ============================================

interface ImageViewerProps {
  /** Path to the image file */
  path: string;
  /** File name (for display) */
  filename: string;
  /** Additional CSS classes */
  className?: string;
}

interface ImageState {
  isLoading: boolean;
  error: string | null;
  naturalWidth: number;
  naturalHeight: number;
}

interface TransformState {
  scale: number;
  translateX: number;
  translateY: number;
}

// ============================================
// Constants
// ============================================

const MIN_ZOOM = 0.1;
const MAX_ZOOM = 10;
const ZOOM_STEP = 0.25;
const ZOOM_WHEEL_FACTOR = 0.001;

// ============================================
// ImageViewer Component
// ============================================

export function ImageViewer({ path, filename, className }: ImageViewerProps) {
  // Image state
  const [imageState, setImageState] = useState<ImageState>({
    isLoading: true,
    error: null,
    naturalWidth: 0,
    naturalHeight: 0,
  });

  // Transform state (zoom and pan)
  const [transform, setTransform] = useState<TransformState>({
    scale: 1,
    translateX: 0,
    translateY: 0,
  });

  // Panning state
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Get the image URL
  const imageUrl = useMemo(() => {
    return api.files.getDownloadUrl(path, true);
  }, [path]);

  // ----------------------------------------
  // Zoom Controls (defined before image loading handlers)
  // ----------------------------------------

  const setZoom = useCallback((newScale: number, centerX?: number, centerY?: number) => {
    setTransform((prev) => {
      const clampedScale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newScale));

      // If center point provided, adjust translation to zoom towards that point
      if (centerX !== undefined && centerY !== undefined) {
        const scaleFactor = clampedScale / prev.scale;
        const newTranslateX = centerX - (centerX - prev.translateX) * scaleFactor;
        const newTranslateY = centerY - (centerY - prev.translateY) * scaleFactor;

        return {
          scale: clampedScale,
          translateX: newTranslateX,
          translateY: newTranslateY,
        };
      }

      return { ...prev, scale: clampedScale };
    });
  }, []);

  const zoomIn = useCallback(() => {
    setZoom(transform.scale + ZOOM_STEP);
  }, [transform.scale, setZoom]);

  const zoomOut = useCallback(() => {
    setZoom(transform.scale - ZOOM_STEP);
  }, [transform.scale, setZoom]);

  const resetZoom = useCallback(() => {
    setTransform({
      scale: 1,
      translateX: 0,
      translateY: 0,
    });
  }, []);

  const fitToContainer = useCallback(() => {
    if (!containerRef.current || !imageRef.current) return;

    const container = containerRef.current;
    const img = imageRef.current;

    // Get dimensions
    const containerWidth = container.clientWidth - 48; // Padding
    const containerHeight = container.clientHeight - 48;
    const imageWidth = img.naturalWidth;
    const imageHeight = img.naturalHeight;

    if (imageWidth === 0 || imageHeight === 0) return;

    // Calculate scale to fit
    const scaleX = containerWidth / imageWidth;
    const scaleY = containerHeight / imageHeight;
    const scale = Math.min(scaleX, scaleY, 1); // Don't scale up beyond 100%

    setTransform({
      scale,
      translateX: 0,
      translateY: 0,
    });
  }, []);

  // ----------------------------------------
  // Image Loading
  // ----------------------------------------

  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setImageState({
      isLoading: false,
      error: null,
      naturalWidth: img.naturalWidth,
      naturalHeight: img.naturalHeight,
    });

    // Auto-fit on load
    fitToContainer();
  }, [fitToContainer]);

  const handleImageError = useCallback(() => {
    setImageState({
      isLoading: false,
      error: 'Failed to load image',
      naturalWidth: 0,
      naturalHeight: 0,
    });
  }, []);

  // ----------------------------------------
  // Pan Controls
  // ----------------------------------------

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return; // Left click only

    setIsPanning(true);
    setPanStart({
      x: e.clientX - transform.translateX,
      y: e.clientY - transform.translateY,
    });

    e.preventDefault();
  }, [transform.translateX, transform.translateY]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isPanning) return;

    setTransform((prev) => ({
      ...prev,
      translateX: e.clientX - panStart.x,
      translateY: e.clientY - panStart.y,
    }));
  }, [isPanning, panStart]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsPanning(false);
  }, []);

  // ----------------------------------------
  // Wheel Zoom
  // ----------------------------------------

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    // Get mouse position relative to container center
    const centerX = e.clientX - rect.left - rect.width / 2;
    const centerY = e.clientY - rect.top - rect.height / 2;

    // Calculate new zoom
    const delta = -e.deltaY * ZOOM_WHEEL_FACTOR;
    const newScale = transform.scale * (1 + delta);

    setZoom(newScale, centerX, centerY);
  }, [transform.scale, setZoom]);

  // ----------------------------------------
  // Download
  // ----------------------------------------

  const handleDownload = useCallback(() => {
    void downloadWorkspaceFile(path, filename);
  }, [filename, path]);

  // ----------------------------------------
  // Keyboard Shortcuts
  // ----------------------------------------

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if this component is focused
      if (!containerRef.current?.contains(document.activeElement)) return;

      switch (e.key) {
        case '+':
        case '=':
          e.preventDefault();
          zoomIn();
          break;
        case '-':
          e.preventDefault();
          zoomOut();
          break;
        case '0':
          e.preventDefault();
          resetZoom();
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          fitToContainer();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [zoomIn, zoomOut, resetZoom, fitToContainer]);

  // ----------------------------------------
  // Render
  // ----------------------------------------

  const zoomPercent = Math.round(transform.scale * 100);

  return (
    <div
      className={cn(
        'h-full w-full flex flex-col',
        'bg-surface-950 dark:bg-surface-950',
        className
      )}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-surface-800 bg-surface-900">
        {/* Left: File info */}
        <div className="flex items-center gap-3">
          <ImageIcon className="h-4 w-4 text-surface-400" />
          <span className="text-sm text-surface-300 font-medium truncate max-w-[200px]">
            {filename}
          </span>
          {imageState.naturalWidth > 0 && (
            <span className="text-xs text-surface-500">
              {imageState.naturalWidth} × {imageState.naturalHeight}
            </span>
          )}
        </div>

        {/* Center: Zoom controls */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={zoomOut}
            disabled={transform.scale <= MIN_ZOOM}
            title="Zoom out (-)"
            className="h-8 w-8"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>

          <span className="text-xs text-surface-400 w-12 text-center tabular-nums">
            {zoomPercent}%
          </span>

          <Button
            variant="ghost"
            size="icon"
            onClick={zoomIn}
            disabled={transform.scale >= MAX_ZOOM}
            title="Zoom in (+)"
            className="h-8 w-8"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>

          <div className="w-px h-4 bg-surface-700 mx-1" />

          <Button
            variant="ghost"
            size="icon"
            onClick={fitToContainer}
            title="Fit to window (F)"
            className="h-8 w-8"
          >
            <Maximize className="h-4 w-4" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={resetZoom}
            title="Reset zoom (0)"
            className="h-8 w-8"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDownload}
            title="Download"
            className="h-8"
          >
            <Download className="h-4 w-4 mr-1" />
            <span className="text-xs">Download</span>
          </Button>
        </div>
      </div>

      {/* Image container */}
      <div
        ref={containerRef}
        className={cn(
          'flex-1 overflow-hidden relative',
          'flex items-center justify-center',
          isPanning ? 'cursor-grabbing' : 'cursor-grab'
        )}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onWheel={handleWheel}
        tabIndex={0}
      >
        {/* Loading state */}
        {imageState.isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-4" />
              <p className="text-surface-400">Loading image...</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {imageState.error && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center max-w-md">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-destructive-900/30 text-destructive-400 mb-4">
                <AlertCircle className="w-8 h-8" />
              </div>
              <h2 className="text-xl font-semibold text-surface-100 mb-2">
                Failed to load image
              </h2>
              <p className="text-surface-400">
                {imageState.error}
              </p>
            </div>
          </div>
        )}

        {/* Checkerboard background for transparency */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(45deg, #1a1a1a 25%, transparent 25%),
              linear-gradient(-45deg, #1a1a1a 25%, transparent 25%),
              linear-gradient(45deg, transparent 75%, #1a1a1a 75%),
              linear-gradient(-45deg, transparent 75%, #1a1a1a 75%)
            `,
            backgroundSize: '20px 20px',
            backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
            backgroundColor: '#0d0d0d',
          }}
        />

        {/* Image */}
        <img
          ref={imageRef}
          src={imageUrl}
          alt={filename}
          className={cn(
            'relative max-w-none select-none',
            'transition-transform duration-75 ease-out',
            imageState.isLoading && 'opacity-0',
            !imageState.isLoading && !imageState.error && 'opacity-100'
          )}
          style={{
            transform: `translate(${transform.translateX}px, ${transform.translateY}px) scale(${transform.scale})`,
            transformOrigin: 'center center',
          }}
          onLoad={handleImageLoad}
          onError={handleImageError}
          draggable={false}
        />

        {/* Pan hint */}
        {!imageState.isLoading && !imageState.error && transform.scale > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 pointer-events-none">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-surface-900/80 text-surface-400 text-xs">
              <Move className="h-3 w-3" />
              <span>Drag to pan</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ImageViewer;
